import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine, LabelList,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Target, RefreshCw, Copy, Lock, AlertTriangle, CheckCircle2, XCircle, Plus, Trash2, Loader2, Info,
  FileSpreadsheet, FileText, Download,
} from "lucide-react";
import { buildDailySummary, fmtDay, fmtLakh, fmtPct, fmtTimeIST } from "@/lib/otdSummary";
import { cellText, exportAllReportsExcel, exportReportExcel, exportReportPdf, fmtMoney } from "@/lib/otdReportExport";

// OTD Command Board — the daily production meeting board. Every number is
// computed live by backend/otd_board.py from Sales Orders, Delivery Challans,
// Transfer Orders, POs, GINs, Production Jobs and QC; the only things typed
// in here are each department's pre-meeting note, action items, department
// owners and the GM closing the meeting.

const REFRESH_MS = 60_000;
const GREEN = "#16A34A";
const RED = "#DC2626";
const AMBER = "#D97706";
const BLUE = "#2563EB";

const STATUS_STYLE = {
  open: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-50 text-blue-700",
  done: "bg-green-50 text-green-700",
  blocked: "bg-red-50 text-red-700",
};
const STATUS_LABEL = { open: "Pending", in_progress: "In Progress", done: "Done", blocked: "Blocked" };
const PRIORITY_STYLE = {
  high: "bg-red-50 text-red-700",
  medium: "bg-amber-50 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};
const RAG = {
  red: { label: "Red", dot: "bg-red-600", text: "text-red-700", desc: "Overdue or blocked past committed date" },
  amber: { label: "Amber", dot: "bg-amber-500", text: "text-amber-700", desc: "Due within 3 days or open tasks" },
  green: { label: "Green", dot: "bg-green-600", text: "text-green-700", desc: "On track" },
};

const errMsg = (e) => formatApiError(e?.response?.data?.detail);
const shortDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

function Panel({ title, right, children, className = "", testId }) {
  return (
    <section className={`bg-white border border-slate-200 rounded-lg p-4 ${className}`} data-testid={testId}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-600">{title}</h2>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

function StatusBadge({ status }) {
  return <Badge className={`${STATUS_STYLE[status] || ""} border-0 font-medium`}>{STATUS_LABEL[status] || status}</Badge>;
}

function PriorityBadge({ priority }) {
  return <Badge className={`${PRIORITY_STYLE[priority] || ""} border-0 font-medium capitalize`}>{priority}</Badge>;
}

// ============================================================ CEO View
// Makes a tile / count open its drill-down report (mouse + keyboard).
function clickableProps(onClick, label) {
  if (!onClick) return {};
  return {
    role: "button", tabIndex: 0, title: `View ${label} report`, onClick,
    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } },
  };
}

function KpiTile({ label, value, sub, tone = "slate", testId, onClick }) {
  const toneCls = { slate: "text-slate-900", red: "text-red-700", green: "text-green-700", amber: "text-amber-700" }[tone];
  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-4${onClick ? " cursor-pointer hover:border-blue-400 hover:shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" : ""}`}
      data-testid={testId} {...clickableProps(onClick, label)}>
      <div className="text-[11px] uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className={`text-2xl font-extrabold mt-1 ${toneCls}`} style={{ fontFamily: "Manrope" }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function otdTone(pct) {
  if (pct === null || pct === undefined) return "slate";
  if (pct >= 100) return "green";
  return pct >= 90 ? "amber" : "red";
}

function ReadinessCard({ card, canEdit, onSubmitted, cutoff, onOpenReport }) {
  const [note, setNote] = useState(card.note || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(card.note || ""); }, [card.note]);

  const submit = async () => {
    if (!note.trim()) { toast.error("Write a short update first"); return; }
    setSaving(true);
    try {
      await api.put(`/otd/readiness/${card.department}`, { note });
      toast.success(`${card.label} update submitted`);
      onSubmitted();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const border = card.ready ? "border-green-300" : "border-red-300";
  const head = card.ready ? "bg-green-50" : "bg-red-50";
  return (
    <div className={`rounded-lg border-2 ${border} bg-white overflow-hidden flex flex-col`} data-testid={`otd-card-${card.department}`}>
      <div className={`${head} px-3 py-2 flex items-start justify-between gap-2`}>
        <div>
          <div className="font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{card.label}</div>
          <div className="text-xs text-slate-600">{card.owner_name || <span className="italic text-slate-400">No owner assigned</span>}</div>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${card.ready ? "text-green-700" : "text-red-700"}`}>
          {card.ready ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {card.ready ? "Ready" : "Not ready"}
        </span>
      </div>
      <div className="px-3 py-2 flex-1 flex flex-col gap-2">
        <div className={`flex items-baseline gap-2${onOpenReport ? " cursor-pointer group rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" : ""}`}
          data-testid={`otd-count-open-${card.department}`} {...clickableProps(onOpenReport, card.count_label)}>
          <span className={`text-2xl font-extrabold text-slate-900${onOpenReport ? " group-hover:text-blue-700 group-hover:underline" : ""}`} style={{ fontFamily: "Manrope" }} data-testid={`otd-count-${card.department}`}>{card.live_count}</span>
          <span className={`text-xs text-slate-500${onOpenReport ? " group-hover:text-blue-700" : ""}`}>{card.count_label}</span>
        </div>
        {card.live_breakdown && Object.keys(card.live_breakdown).length > 0 && (
          <div className="text-[11px] text-slate-500">
            {Object.entries(card.live_breakdown).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        )}
        {canEdit ? (
          <>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000}
              placeholder={`Today's update (before ${cutoff})`} className="text-sm" data-testid={`otd-note-${card.department}`} />
            <Button size="sm" variant="outline" onClick={submit} disabled={saving} data-testid={`otd-note-submit-${card.department}`}>
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {card.submitted_at ? "Update note" : "Submit update"}
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{card.note || <span className="italic text-slate-400">No update yet</span>}</p>
        )}
        {card.submitted_at && (
          <div className="text-[11px] text-slate-500">
            Submitted {fmtTimeIST(card.submitted_at)}{card.submitted_by_name ? ` by ${card.submitted_by_name}` : ""}
            {!card.ready && " (after cut-off)"}
          </div>
        )}
      </div>
    </div>
  );
}

// Hatched fill for the "late" segment — red/green alone isn't separable for
// red-green colour-blind viewers, so the late share also carries a texture.
function LatePattern() {
  return (
    <defs>
      <pattern id="otd-late-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
        <rect width="6" height="6" fill={RED} />
        <line x1="0" y1="0" x2="0" y2="6" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.45" />
      </pattern>
    </defs>
  );
}

function OtdTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900 mb-1">{fmtDay(label)}</div>
      {d.due ? (
        <>
          <div className="text-slate-700">OTD: <b>{fmtPct(d.otd_pct)}</b></div>
          <div className="text-slate-600">On time: {d.on_time} · Late: {d.late} · Due: {d.due}</div>
        </>
      ) : <div className="text-slate-500">No orders due</div>}
    </div>
  );
}

function DailyOtdChart({ data, height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <LatePattern />
        <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
        <Tooltip content={<OtdTooltip />} cursor={{ fill: "rgba(37,99,235,0.06)" }} />
        <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="on_time_pct" name="On time %" stackId="otd" fill={GREEN} stroke="#ffffff" strokeWidth={1} isAnimationActive={false} />
        <Bar dataKey="late_pct" name="Late %" stackId="otd" fill="url(#otd-late-hatch)" stroke="#ffffff" strokeWidth={1} radius={[4, 4, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CeoView({ board, config, reload, isAdmin }) {
  const [closing, setClosing] = useState(false);
  const [report, setReport] = useState(null);   // drill-down report key open in the drawer
  const k = board.kpis;
  const m = board.meeting;
  const mine = new Set(board.my_departments || []);

  const closeMeeting = async () => {
    if (k.red_orders_without_action > 0 &&
      !window.confirm(`${k.red_orders_without_action} red order(s) still have no open action. Close the meeting anyway?`)) return;
    setClosing(true);
    try {
      await api.post("/otd/meeting/close");
      toast.success("Today's meeting closed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setClosing(false);
    }
  };

  const copySummary = async () => {
    const ok = await copyText(buildDailySummary(board));
    if (ok) toast.success("Daily summary copied — paste it into WhatsApp");
    else toast.error("Couldn't copy to the clipboard");
  };

  return (
    <div className="space-y-5">
      {/* GM sign-off */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="GM Sign-off" className="lg:col-span-2" testId="otd-signoff">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-600">Meeting status:</span>
            {m.status === "closed" ? (
              <Badge className="bg-green-50 text-green-700 border-0 font-semibold" data-testid="otd-meeting-status">
                <Lock className="w-3 h-3 mr-1" /> Closed
              </Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-700 border-0 font-semibold" data-testid="otd-meeting-status">Open</Badge>
            )}
            {m.status === "closed" && (
              <span className="text-xs text-slate-500" data-testid="otd-meeting-closed-at">
                Closed at {fmtTimeIST(m.closed_at)}{m.closed_by_name ? ` by ${m.closed_by_name}` : ""}
              </span>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={copySummary} data-testid="otd-copy-summary">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Daily Summary
              </Button>
              {isAdmin && m.status !== "closed" && (
                <Button size="sm" onClick={closeMeeting} disabled={closing} className="bg-blue-600 hover:bg-blue-700" data-testid="otd-close-meeting">
                  {closing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
                  Close Today's Meeting
                </Button>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2 rounded-md bg-blue-50 text-blue-800 text-sm px-3 py-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            GM closes the meeting here after every red order has an owner, action and date.
          </div>
          {m.close_overdue && (
            <div className="mt-2 flex items-start gap-2 rounded-md bg-red-600 text-white text-sm font-semibold px-3 py-2" data-testid="otd-close-overdue">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Meeting not closed by {m.close_by} AM — close it once every red order has an owner, action and date.
            </div>
          )}
          {k.red_orders_without_action > 0 && m.status !== "closed" && (
            <div className="mt-2 text-xs text-red-700">{k.red_orders_without_action} red order(s) still need an action.</div>
          )}
        </Panel>

        <Panel title="Pending orders by status" testId="otd-rag">
          <ul className="space-y-2">
            {["red", "amber", "green"].map((key) => (
              <li key={key} className="flex items-center gap-3 cursor-pointer rounded hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                data-testid={`otd-rag-open-${key}`}
                {...clickableProps(() => setReport(RAG_REPORT[key]), `${RAG[key].label} orders`)}>
                <span className={`w-3 h-3 rounded-full ${RAG[key].dot}`} />
                <span className={`font-semibold w-14 ${RAG[key].text}`}>{RAG[key].label}</span>
                <span className="text-xl font-extrabold text-slate-900 w-10 text-right" style={{ fontFamily: "Manrope" }} data-testid={`otd-rag-${key}`}>
                  {board.pending_status[key]}
                </span>
                <span className="text-xs text-slate-500">{RAG[key].desc}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {/* KPI summary bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="otd-kpis">
        <KpiTile testId="otd-kpi-mtd" label="OTD month to date" value={fmtPct(k.otd_mtd.otd_pct)} tone={otdTone(k.otd_mtd.otd_pct)}
          sub={`${k.otd_mtd.on_time}/${k.otd_mtd.due} orders on time`} />
        <KpiTile testId="otd-kpi-7d" label="OTD last 7 days" value={fmtPct(k.otd_7d.otd_pct)} tone={otdTone(k.otd_7d.otd_pct)}
          sub={`${k.otd_7d.on_time}/${k.otd_7d.due} orders on time`} />
        <KpiTile testId="otd-kpi-overdue" label="Overdue orders" value={k.overdue_orders} tone={k.overdue_orders ? "red" : "green"}
          onClick={() => setReport("overdue")}
          sub="Past committed date, not dispatched" />
        <KpiTile testId="otd-kpi-red" label="Red orders" value={k.red_orders} tone={k.red_orders ? "red" : "green"}
          onClick={() => setReport("red")}
          sub="> 3 days late — GM intervention" />
        <KpiTile testId="otd-kpi-var" label="Value at risk" value={fmtLakh(k.value_at_risk_lakh)} tone={k.value_at_risk_lakh ? "red" : "slate"}
          onClick={() => setReport("value_at_risk")}
          sub={Object.keys(k.value_at_risk_other || {}).length
            ? `+ ${Object.entries(k.value_at_risk_other).map(([c, v]) => `${c} ${v.toLocaleString("en-IN")}`).join(", ")}`
            : "Grand total of overdue SOs"} />
        <KpiTile testId="otd-kpi-blocked" label="Blocked tasks" value={k.blocked_tasks} tone={k.blocked_tasks ? "red" : "green"}
          sub="Action items marked Blocked" />
      </div>

      {/* Pre-meeting readiness */}
      <Panel title="Pre-meeting readiness" testId="otd-readiness"
        right={<span className="text-xs text-slate-500">Green = update in before {config.readiness_cutoff} AM</span>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {board.readiness.map((c) => (
            <ReadinessCard key={c.department} card={c} canEdit={mine.has(c.department)} onSubmitted={reload} cutoff={config.readiness_cutoff}
              onOpenReport={() => setReport(c.department)} />
          ))}
        </div>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Department tasks on pending orders" testId="otd-dept-tasks">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 pr-2">Department</th>
                  <th className="text-left py-2 pr-2">Owner</th>
                  <th className="text-right py-2 px-2">Done</th>
                  <th className="text-right py-2 px-2">Pending</th>
                  <th className="text-right py-2 pl-2">Blocked</th>
                </tr>
              </thead>
              <tbody>
                {board.dept_tasks.map((r) => (
                  <tr key={r.department} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-2 font-medium text-slate-900">{r.label}</td>
                    <td className="py-2 pr-2 text-slate-600">{r.owner_name || "—"}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-green-700">{r.done}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-900">{r.pending}</td>
                    <td className={`py-2 pl-2 text-right tabular-nums ${r.blocked ? "text-red-700 font-semibold" : "text-slate-400"}`}>{r.blocked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Daily OTD — last 14 days" testId="otd-daily-chart">
          <DailyOtdChart data={board.daily_otd} />
          <p className="text-[11px] text-slate-500 mt-1">
            Orders due each day: delivery challan dispatch date vs the Sales Order's committed delivery date.
          </p>
        </Panel>
      </div>

      {board.red_orders.length > 0 && (
        <Panel title="Red orders" testId="otd-red-orders">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 pr-2">SO No.</th>
                  <th className="text-left py-2 pr-2">Customer</th>
                  <th className="text-left py-2 pr-2">Committed</th>
                  <th className="text-right py-2 px-2">Days overdue</th>
                  <th className="text-right py-2 px-2">Value</th>
                  <th className="text-left py-2 pl-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {board.red_orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-2 mono font-semibold">{o.code}</td>
                    <td className="py-2 pr-2">{o.customer_name}</td>
                    <td className="py-2 pr-2">{fmtDay(o.committed)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-red-700 font-semibold">{o.overdue_days}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{o.currency} {o.grand_total.toLocaleString("en-IN")}</td>
                    <td className="py-2 pl-2">
                      {o.blocked ? <StatusBadge status="blocked" />
                        : o.has_action ? <span className="text-xs text-green-700">Action open</span>
                          : <span className="text-xs text-red-700 font-semibold">No action yet</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <ReportDrawer reportKey={report} onClose={() => setReport(null)} />
    </div>
  );
}

// ============================================================ Drill-down reports
// Clicking a count opens the detail behind it (GET /otd/drilldown/{key}),
// with Excel and PDF export of exactly what is listed.
const RAG_REPORT = { red: "rag_red", amber: "rag_amber", green: "on_track" };

function ReportDrawer({ reportKey, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!reportKey) return undefined;
    let live = true;
    setData(null);
    setErr("");
    api.get(`/otd/drilldown/${reportKey}`)
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setErr(errMsg(e)); });
    return () => { live = false; };
  }, [reportKey]);

  const doExport = (fn) => {
    try { fn(data, data.generated_at); } catch (e) { toast.error(`Export failed: ${e.message || e}`); }
  };

  return (
    <Sheet open={!!reportKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto" data-testid="otd-report-drawer">
        <SheetHeader className="pr-8">
          <SheetTitle style={{ fontFamily: "Manrope" }} data-testid="otd-report-title">
            {data ? data.title : "Loading report…"}
          </SheetTitle>
          <SheetDescription>
            {data ? `${data.count} record(s) · live as of ${fmtTimeIST(data.generated_at)} IST` : " "}
          </SheetDescription>
        </SheetHeader>
        {data && (
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => doExport(exportReportExcel)} data-testid="otd-report-excel">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Export Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => doExport(exportReportPdf)} data-testid="otd-report-pdf">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Export PDF
            </Button>
          </div>
        )}
        {err && <div className="mt-4 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{err}</div>}
        {!data && !err && <Loader2 className="w-5 h-5 mt-6 animate-spin text-slate-400" />}
        {data?.totals && Object.keys(data.totals).length > 0 && (
          <div className="mt-4 text-sm text-slate-700" data-testid="otd-report-totals">
            Total: {Object.entries(data.totals).map(([c, v]) => fmtMoney({ currency: c }, v)).join(" + ")}
          </div>
        )}
        {data?.sections.map((s) => (
          <div key={s.title} className="mt-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-600 mb-2">{s.title} ({s.rows.length})</h3>
            {s.rows.length === 0 ? <div className="text-sm text-slate-500 py-4">No records.</div> : (
              <div className="overflow-x-auto border border-slate-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      {s.columns.map((c) => (
                        <th key={c.key} className={`py-2 px-2 whitespace-nowrap ${c.type === "number" || c.type === "money" ? "text-right" : "text-left"}`}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.rows.map((r, i) => (
                      <tr key={`${r.id || ""}-${i}`} className="border-b border-slate-100 last:border-0 align-top">
                        {s.columns.map((c) => (
                          <td key={c.key} className={`py-1.5 px-2 ${c.type === "number" || c.type === "money" ? "text-right tabular-nums whitespace-nowrap" : c.type === "date" ? "whitespace-nowrap" : ""}`}>
                            {cellText(c, r)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </SheetContent>
    </Sheet>
  );
}

// ============================================================ Actions
function StatusButtons({ action, onChange, busy }) {
  const opts = [["in_progress", "In Progress"], ["done", "Done"], ["blocked", "Blocked"]];
  return (
    <div className="flex flex-wrap gap-1">
      {opts.map(([k, label]) => (
        <Button key={k} size="sm" variant={action.status === k ? "default" : "outline"} disabled={busy || action.status === k}
          className={`h-7 px-2 text-xs ${action.status === k ? "" : ""}`}
          onClick={() => onChange(action, k)} data-testid={`otd-action-${action.id}-${k}`}>
          {label}
        </Button>
      ))}
    </div>
  );
}

function useStatusChange(onDone) {
  const [busyId, setBusyId] = useState(null);
  const change = async (action, status) => {
    let note;
    if (status === "blocked") {
      note = window.prompt("What is blocking this action?", action.note || "");
      if (note === null) return;
    }
    setBusyId(action.id);
    try {
      await api.patch(`/otd/actions/${action.id}`, note !== undefined ? { status, note } : { status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };
  return [busyId, change];
}

function ActionsTable({ rows, onStatus, busyId, onDelete, showDept = true }) {
  if (!rows.length) return <div className="text-sm text-slate-500 py-6 text-center">No action items.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <th className="text-left py-2 pr-2">Action</th>
            {showDept && <th className="text-left py-2 pr-2">Department</th>}
            <th className="text-left py-2 pr-2">Owner</th>
            <th className="text-left py-2 pr-2">Due</th>
            <th className="text-left py-2 pr-2">Priority</th>
            <th className="text-left py-2 pr-2">Status</th>
            <th className="text-left py-2 pl-2">Update</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-b border-slate-100 last:border-0 align-top" data-testid={`otd-action-row-${a.id}`}>
              <td className="py-2 pr-2 max-w-md">
                <div className="text-slate-900">{a.description}</div>
                {a.so_code && <div className="text-[11px] text-slate-500 mono">{a.so_code}{a.so_customer ? ` · ${a.so_customer}` : ""}</div>}
                {a.note && <div className="text-[11px] text-slate-500 italic">“{a.note}”</div>}
              </td>
              {showDept && <td className="py-2 pr-2 whitespace-nowrap">{a.department_label}</td>}
              <td className="py-2 pr-2 whitespace-nowrap">{a.owner_name || a.owner_email || "—"}</td>
              <td className={`py-2 pr-2 whitespace-nowrap ${a.overdue ? "text-red-700 font-semibold" : ""}`}>{a.due_date ? fmtDay(a.due_date) : "—"}</td>
              <td className="py-2 pr-2"><PriorityBadge priority={a.priority} /></td>
              <td className="py-2 pr-2"><StatusBadge status={a.status} /></td>
              <td className="py-2 pl-2">
                <div className="flex items-center gap-1">
                  {a.can_update_status && <StatusButtons action={a} onChange={onStatus} busy={busyId === a.id} />}
                  {onDelete && a.can_edit && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-red-700" onClick={() => onDelete(a)}
                      title="Delete action" data-testid={`otd-action-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MyTasks({ tick, onChanged }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get("/otd/my-tasks").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(load, [load, tick]);
  const [busyId, change] = useStatusChange(() => { load(); onChanged(); });
  return (
    <Panel title="My pending & blocked actions" testId="otd-my-tasks">
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        : <ActionsTable rows={rows} onStatus={change} busyId={busyId} />}
    </Panel>
  );
}

const EMPTY_ACTION = { description: "", department: "", owner_email: "", due_date: "", priority: "medium", so_id: "" };

function NewActionDialog({ open, onOpenChange, config, soOptions, onCreated }) {
  const [form, setForm] = useState(EMPTY_ACTION);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(EMPTY_ACTION); }, [open]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const pickDept = (dept) => {
    const d = config.departments.find((x) => x.key === dept);
    setForm((f) => ({ ...f, department: dept, owner_email: f.owner_email || d?.owner_email || "" }));
  };

  const save = async () => {
    if (!form.description.trim()) { toast.error("Describe the action"); return; }
    if (!form.department) { toast.error("Pick a department"); return; }
    setSaving(true);
    try {
      await api.post("/otd/actions", {
        ...form,
        owner_email: form.owner_email || "",
        due_date: form.due_date || null,
        so_id: form.so_id || null,
      });
      toast.success("Action added");
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New action item</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set("description")(e.target.value)} data-testid="otd-new-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Department</Label>
              <Select value={form.department || undefined} onValueChange={pickDept}>
                <SelectTrigger data-testid="otd-new-department"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {config.departments.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Owner</Label>
              <Select value={form.owner_email || "none"} onValueChange={(v) => set("owner_email")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="otd-new-owner"><SelectValue placeholder="Department owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Department owner</SelectItem>
                  {config.users.filter((u) => u.email).map((u) => (
                    <SelectItem key={u.email} value={u.email}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="otd-new-due" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Priority</Label>
              <Select value={form.priority} onValueChange={set("priority")}>
                <SelectTrigger data-testid="otd-new-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config.priorities.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Sales Order (optional)</Label>
            <Select value={form.so_id || "none"} onValueChange={(v) => set("so_id")(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="otd-new-so"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not linked to an order</SelectItem>
                {soOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} · {s.customer}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="otd-new-save">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Add action
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionsTab({ config, tick, onChanged }) {
  const [rows, setRows] = useState(null);
  const [dept, setDept] = useState("all");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [soOptions, setSoOptions] = useState([]);

  const load = useCallback(() => {
    api.get("/otd/actions", { params: { department: dept === "all" ? "" : dept, status: status === "all" ? "" : status } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [dept, status]);
  useEffect(load, [load, tick]);

  useEffect(() => {
    if (!open || soOptions.length) return;
    api.get("/otd/sales-orders").then((r) => {
      const seen = new Map();
      for (const row of r.data.rows) if (!seen.has(row.cso_id)) seen.set(row.cso_id, { id: row.cso_id, code: row.so_code, customer: row.customer_name });
      setSoOptions([...seen.values()]);
    }).catch(() => {});
  }, [open, soOptions.length]);

  const refresh = () => { load(); onChanged(); };
  const [busyId, change] = useStatusChange(refresh);
  const remove = async (a) => {
    if (!window.confirm("Delete this action item?")) return;
    try {
      await api.delete(`/otd/actions/${a.id}`);
      toast.success("Action deleted");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Panel title={config.is_admin ? "All actions" : "Actions — mine & my departments"} testId="otd-actions"
      right={<Button size="sm" onClick={() => setOpen(true)} data-testid="otd-new-action"><Plus className="w-3.5 h-3.5 mr-1.5" /> New action</Button>}>
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="w-48">
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="otd-actions-filter-dept"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {config.departments.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="otd-actions-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {config.statuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        : <ActionsTable rows={rows} onStatus={change} busyId={busyId} onDelete={remove} />}
      <NewActionDialog open={open} onOpenChange={setOpen} config={config} soOptions={soOptions} onCreated={refresh} />
    </Panel>
  );
}

// ============================================================ Sales Orders
function SalesOrdersTab({ tick }) {
  const [filters, setFilters] = useState({ status: "all", customer: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/otd/sales-orders", { params: { ...filters, status: filters.status === "all" ? "" : filters.status } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, tick]);
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <Panel title="Open Sales Orders" testId="otd-sales-orders"
      right={loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div>
          <Label className="text-xs text-slate-600">Status</Label>
          <Select value={filters.status} onValueChange={set("status")}>
            <SelectTrigger data-testid="otd-so-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {["red", "amber", "green"].map((k) => <SelectItem key={k} value={k}>{RAG[k].label} — {RAG[k].desc}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Customer</Label>
          <Input list="otd-customers" value={filters.customer} onChange={(e) => set("customer")(e.target.value)} placeholder="Any customer" data-testid="otd-so-filter-customer" />
          <datalist id="otd-customers">{(data?.customers || []).map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Delivery date from</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="otd-so-filter-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Delivery date to</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="otd-so-filter-to" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 pr-2">SO Number</th>
              <th className="text-left py-2 pr-2">Customer</th>
              <th className="text-left py-2 pr-2">Part</th>
              <th className="text-right py-2 px-2">Ordered</th>
              <th className="text-right py-2 px-2">Packed</th>
              <th className="text-right py-2 px-2">Balance</th>
              <th className="text-left py-2 px-2">Delivery Date</th>
              <th className="text-left py-2 px-2">Status</th>
              <th className="text-right py-2 pl-2">Days Overdue</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`otd-so-row-${r.id}`}>
                <td className="py-2 pr-2 mono font-semibold whitespace-nowrap">{r.so_code}</td>
                <td className="py-2 pr-2">{r.customer_name}</td>
                <td className="py-2 pr-2">
                  <div className="mono text-xs">{r.part_code}</div>
                  <div className="text-[11px] text-slate-500">{r.part_name}</div>
                </td>
                <td className="py-2 px-2 text-right tabular-nums">{r.ordered_qty}</td>
                <td className="py-2 px-2 text-right tabular-nums">{r.packed_qty}</td>
                <td className="py-2 px-2 text-right tabular-nums font-semibold">{r.balance_qty}</td>
                <td className="py-2 px-2 whitespace-nowrap">{r.delivery_date ? fmtDay(r.delivery_date) : "—"}</td>
                <td className="py-2 px-2 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1.5 ${RAG[r.status].text}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${RAG[r.status].dot}`} /> {RAG[r.status].label}
                  </span>
                  <div className="text-[11px] text-slate-500">{r.stage}</div>
                </td>
                <td className={`py-2 pl-2 text-right tabular-nums ${r.days_overdue ? "text-red-700 font-semibold" : "text-slate-400"}`}>{r.days_overdue}</td>
              </tr>
            ))}
            {data && !data.rows.length && (
              <tr><td colSpan={9} className="py-6 text-center text-slate-500">No open Sales Orders match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ============================================================ KPIs
function KpisTab({ tick }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/otd/kpis", { params: { days: 30 } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tick]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  // A department with no actions has no completion rate — say so rather than plot 0%.
  const completion = data.dept_completion.map((d) => ({
    ...d,
    completion: d.completion_pct ?? 0,
    bar_label: d.total ? `${d.completion_pct}% (${d.done}/${d.total})` : "No tasks",
  }));
  const axis = { tick: { fontSize: 11, fill: "#64748b" }, axisLine: false, tickLine: false };
  return (
    <div className="space-y-4">
      <Panel title="OTD % trend — last 30 days" testId="otd-kpi-trend"
        right={<span className="text-xs text-slate-500">Period OTD {fmtPct(data.period_otd.otd_pct)} ({data.period_otd.on_time}/{data.period_otd.due})</span>}>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.otd_trend} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDay} {...axis} interval="preserveStartEnd" minTickGap={24} />
            <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v) => `${v}%`} {...axis} />
            <ReferenceLine y={100} stroke={GREEN} strokeDasharray="4 4" label={{ value: "Target 100%", position: "insideTopRight", fontSize: 11, fill: "#475569" }} />
            <Tooltip content={<OtdTooltip />} />
            {/* Days with nothing due stay as gaps — no trend is implied across them. */}
            <Line type="linear" dataKey="otd_pct" name="OTD %" stroke={BLUE} strokeWidth={2}
              dot={{ r: 4, strokeWidth: 0, fill: BLUE }} activeDot={{ r: 6 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Department-wise task completion" testId="otd-kpi-dept">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={completion} layout="vertical" margin={{ top: 4, right: 80, left: 8, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} {...axis} />
              <YAxis type="category" dataKey="label" width={110} {...axis} />
              <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }}
                formatter={(_v, _n, p) => [p.payload.bar_label, "Completion"]} />
              <Bar dataKey="completion" name="Completion %" fill={BLUE} radius={[0, 4, 4, 0]} barSize={18} isAnimationActive={false}>
                <LabelList dataKey="bar_label" position="right" fontSize={11} fill="#475569" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Overdue orders — last 30 days" testId="otd-kpi-overdue-trend">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.overdue_trend} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDay} {...axis} interval="preserveStartEnd" minTickGap={24} />
              <YAxis allowDecimals={false} {...axis} />
              <Tooltip labelFormatter={fmtDay} formatter={(v) => [v, "Overdue orders"]} />
              <Line type="stepAfter" dataKey="overdue" name="Overdue orders" stroke={AMBER} strokeWidth={2}
                dot={false} activeDot={{ r: 5 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}

// ============================================================ Owners
function OwnersTab({ config, isAdmin, onSaved }) {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState("");
  const load = useCallback(() => {
    api.get("/otd/owners").then((r) => {
      setRows(r.data);
      setDrafts(Object.fromEntries(r.data.map((o) => [o.department, { owner_name: o.owner_name, owner_email: o.owner_email }])));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(load, [load]);

  const setDraft = (dept, patch) => setDrafts((d) => ({ ...d, [dept]: { ...d[dept], ...patch } }));
  const pickUser = (dept, email) => {
    if (email === "none") { setDraft(dept, { owner_email: "" }); return; }
    const u = config.users.find((x) => x.email === email);
    setDraft(dept, { owner_email: email, owner_name: u?.name || drafts[dept]?.owner_name || "" });
  };
  const save = async (dept) => {
    setSaving(dept);
    try {
      await api.put(`/otd/owners/${dept}`, drafts[dept]);
      toast.success("Owner saved");
      load();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving("");
    }
  };

  if (!rows) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  return (
    <Panel title="Department owners" testId="otd-owners"
      right={!isAdmin && <span className="text-xs text-slate-500">Only the GM (admin) can change owners</span>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 pr-2 w-44">Department</th>
              <th className="text-left py-2 pr-2">ERP user</th>
              <th className="text-left py-2 pr-2">Owner name</th>
              {isAdmin && <th className="py-2 pl-2" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.department} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-2 font-medium text-slate-900">{o.label}</td>
                {isAdmin ? (
                  <>
                    <td className="py-2 pr-2 min-w-[14rem]">
                      <Select value={drafts[o.department]?.owner_email || "none"} onValueChange={(v) => pickUser(o.department, v)}>
                        <SelectTrigger data-testid={`otd-owner-user-${o.department}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not linked to a login</SelectItem>
                          {config.users.filter((u) => u.email).map((u) => (
                            <SelectItem key={u.email} value={u.email}>{u.name || u.email} · {u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 pr-2 min-w-[12rem]">
                      <Input value={drafts[o.department]?.owner_name || ""} onChange={(e) => setDraft(o.department, { owner_name: e.target.value })}
                        placeholder="Owner name" data-testid={`otd-owner-name-${o.department}`} />
                    </td>
                    <td className="py-2 pl-2 text-right">
                      <Button size="sm" onClick={() => save(o.department)} disabled={saving === o.department} data-testid={`otd-owner-save-${o.department}`}>
                        {saving === o.department && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Save
                      </Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-2 pr-2 text-slate-600">{o.owner_email || "—"}</td>
                    <td className="py-2 pr-2">{o.owner_name || <span className="italic text-slate-400">Not assigned</span>}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-3">
        Linking an owner to their ERP login lets them submit the department card and see that department's actions under My Tasks.
      </p>
    </Panel>
  );
}

// ============================================================ Page
export default function OtdCommandBoard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("ceo");
  const [config, setConfig] = useState(null);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const loadConfig = useCallback(() => {
    api.get("/otd/config").then((r) => setConfig(r.data)).catch((e) => setError(errMsg(e)));
  }, []);
  const loadBoard = useCallback(() => {
    setLoading(true);
    api.get("/otd/board")
      .then((r) => { setBoard(r.data); setError(""); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadBoard(); }, [loadBoard, tick]);
  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    const id = setInterval(refresh, REFRESH_MS);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(id); window.removeEventListener("focus", refresh); };
  }, []);

  const refreshAll = () => { loadConfig(); setTick((t) => t + 1); };
  const [exporting, setExporting] = useState(false);
  const exportAll = async () => {
    setExporting(true);
    try {
      const r = await api.get("/otd/drilldown");
      exportAllReportsExcel(r.data.reports, r.data.generated_at);
      toast.success("All reports exported");
    } catch (e) {
      toast.error(e?.response ? errMsg(e) : `Export failed: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  };
  const today = useMemo(() => (board?.date ? fmtDay(board.date) : ""), [board?.date]);

  return (
    <div className="space-y-5" data-testid="otd-board-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Daily production meeting</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Target className="w-7 h-7 text-blue-600" /> OTD Command Board
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="otd-today">{today}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" size="sm" variant="outline" onClick={refreshAll} disabled={loading} data-testid="otd-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {tab === "ceo" && (
            <Button type="button" size="sm" variant="outline" onClick={exportAll} disabled={exporting} data-testid="otd-export-all">
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Export All Reports
            </Button>
          )}
          <span className="rounded-md bg-slate-900 text-white px-3 py-2 text-sm font-extrabold tracking-wide" style={{ fontFamily: "Manrope" }} data-testid="otd-target-badge">
            TARGET OTD: 100%
          </span>
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="ceo" data-testid="otd-tab-ceo">CEO View</TabsTrigger>
          <TabsTrigger value="my" data-testid="otd-tab-my">My Tasks</TabsTrigger>
          <TabsTrigger value="so" data-testid="otd-tab-so">Sales Orders</TabsTrigger>
          <TabsTrigger value="actions" data-testid="otd-tab-actions">Actions</TabsTrigger>
          <TabsTrigger value="kpis" data-testid="otd-tab-kpis">KPIs</TabsTrigger>
          <TabsTrigger value="owners" data-testid="otd-tab-owners">Owners</TabsTrigger>
        </TabsList>

        <TabsContent value="ceo" className="mt-4">
          {board && config ? <CeoView board={board} config={config} reload={refreshAll} isAdmin={isAdmin} />
            : !error && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
        </TabsContent>
        <TabsContent value="my" className="mt-4">
          {tab === "my" && <MyTasks tick={tick} onChanged={loadBoard} />}
        </TabsContent>
        <TabsContent value="so" className="mt-4">
          {tab === "so" && <SalesOrdersTab tick={tick} />}
        </TabsContent>
        <TabsContent value="actions" className="mt-4">
          {tab === "actions" && config && <ActionsTab config={config} tick={tick} onChanged={loadBoard} />}
        </TabsContent>
        <TabsContent value="kpis" className="mt-4">
          {tab === "kpis" && <KpisTab tick={tick} />}
        </TabsContent>
        <TabsContent value="owners" className="mt-4">
          {tab === "owners" && config && <OwnersTab config={config} isAdmin={isAdmin} onSaved={refreshAll} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
